import type { PlanIntro } from "./plan-intro";

// The runtime half of intro prices and trials (see plan-intro.ts), with no schema library, so a
// creator site's plan cards stay small.

/**
 * The lowest intro price, in cents, the same floor as a paid plan's own price. A free first
 * month is a trial, not an intro, and Stripe charges nothing for an invoice under its minimum
 * charge (it adds the amount to the next one), so a lower intro would never bill as its card
 * says.
 */
export const minIntroCents = 100;

/**
 * The intro that applies to a plan at this price and interval, or null when none does: one
 * from minIntroCents up to below the plan's price. Checkout's coupon, plan cards and order
 * summaries read the intro through this, so one saved lower before is never shown or billed.
 */
export function planIntroOffer(plan: {
  priceCents: number;
  interval: string;
  intro?: PlanIntro | null;
}): PlanIntro | null {
  const intro = plan.intro;
  if (!intro || (plan.interval !== "month" && plan.interval !== "year")) return null;
  if (intro.priceCents < minIntroCents || intro.priceCents >= plan.priceCents) return null;
  // A yearly plan's intro covers whole years: one "month" of intro is its first payment.
  return plan.interval === "year" ? { ...intro, months: 1 } : intro;
}

/** The longest free trial a plan offers, in days. */
export const maxTrialDays = 30;

/**
 * A recurring plan's free trial: new members pay nothing for its first days, then the plan's
 * price. Null for a plan without one, a one-time plan, or a plan with an intro price, since a
 * plan offers newcomers one or the other. Who gets it follows the intro's rule
 * (planIntroEligible).
 */
export function planTrialOffer(plan: {
  priceCents: number;
  interval: string;
  intro?: PlanIntro | null;
  trialDays?: number | null;
}): number | null {
  if (!plan.trialDays || (plan.interval !== "month" && plan.interval !== "year")) return null;
  return planIntroOffer(plan) ? null : plan.trialDays;
}

/** The discount checkout applies to each intro payment, in cents. */
export function planIntroDiscountCents(plan: {
  priceCents: number;
  interval: string;
  intro?: PlanIntro | null;
}) {
  const intro = planIntroOffer(plan);
  return intro ? plan.priceCents - intro.priceCents : 0;
}

/** What the intro price pays for, as a card and a checkout say it: "first month", "first year". */
export function planIntroTerm(plan: { interval: string }, intro: PlanIntro) {
  return plan.interval === "year"
    ? "first year"
    : intro.months === 1
      ? "first month"
      : `first ${intro.months} months`;
}

// A recurring membership that was paid for; a checkout that never completed leaves the others.
const unpaid = new Set(["incomplete", "incomplete_expired", "pending"]);

/**
 * Whether a member's checkout gets the plan's intro price. It's for joining: not for paid time
 * moved over from Patreon (a checkout with a trial), and not for someone who has subscribed to
 * this creator before (on Stripe or in their app), who could otherwise rejoin at the intro price.
 * One-time passes and courses don't count.
 */
export function planIntroEligible(
  grants: readonly { provider: string; providerId: string; status: string }[],
  trialEnd?: number,
) {
  return (
    !trialEnd &&
    !grants.some(
      (grant) =>
        !unpaid.has(grant.status) &&
        (grant.provider === "apple" ||
          grant.provider === "google" ||
          (grant.provider === "stripe" && grant.providerId.startsWith("sub_"))),
    )
  );
}

/**
 * The Stripe coupon for a plan's intro: an amount off each intro payment, repeating for the
 * intro months of a monthly plan and applied once to a yearly plan's first payment.
 */
export function planIntroCoupon(plan: {
  priceCents: number;
  currency: string;
  interval: string;
  intro?: PlanIntro | null;
}) {
  const intro = planIntroOffer(plan);
  if (!intro) return null;
  return {
    amountOffCents: plan.priceCents - intro.priceCents,
    currency: plan.currency,
    ...(plan.interval === "year"
      ? { duration: "once" as const }
      : intro.months === 1
        ? { duration: "once" as const }
        : { duration: "repeating" as const, durationInMonths: intro.months }),
    // Shown on Stripe's checkout page and invoices, beside the amount it takes off.
    name: `Intro price, ${planIntroTerm(plan, intro)}`,
  };
}

export type PlanIntroCoupon = NonNullable<ReturnType<typeof planIntroCoupon>>;

/**
 * The id of a plan's intro coupon on the creator's Stripe account. The same plan, discount and
 * duration always name the same coupon, so checkout creates each one once, and a changed intro
 * or price names a new one instead of reusing a coupon with other terms.
 */
export function planIntroCouponId(planId: string, coupon: PlanIntroCoupon) {
  const duration =
    coupon.duration === "repeating" ? `${coupon.durationInMonths}m` : coupon.duration;
  return `drate-intro-${planId.replace(/[^\w-]/g, "")}-${coupon.amountOffCents}${coupon.currency}-${duration}`;
}

/**
 * How much a yearly plan saves against twelve months of a monthly plan with the same title,
 * as a whole percentage, or null when it doesn't save anything worth saying.
 */
export function annualSavingsPercent(monthlyCents: number, yearlyCents: number) {
  if (monthlyCents <= 0 || yearlyCents <= 0) return null;
  const full = monthlyCents * 12;
  const percent = Math.floor(((full - yearlyCents) / full) * 100);
  return percent >= 1 ? percent : null;
}
